import * as React from 'react'
import {IPopupDispatcher} from '../../core/interfaces/dispatchers'
import {Fill, Feature, Stroke, Style, VectorLayer, VectorSource} from '../../core/models'
import {getMapService} from '../../core/reducers/map'
import {MapService} from '../../core/services'
import appConfig from '../../reducers/appConfig'
import CircleStyle from 'ol/style/Circle'
import {unByKey} from 'ol/Observable'
import {getArea} from 'ol/sphere'
import Polygon from 'ol/geom/Polygon'
import {Collection} from 'ol'
import Overlay from 'ol/Overlay'
import {EventsKey} from 'ol/events'
import OlFeature from 'ol/Feature'
import {Draw, Snap} from 'ol/interaction'

const formatArea = (polygon: Polygon): string => {
  const area = getArea(polygon)
  return Math.round(area * 100) / 100 + ' м<sup>2</sup>'
}

interface Props {
  left: string
  popupDispatcher: IPopupDispatcher
  isAreaButtonActive: boolean
  handleIsAreaButtonStateOnClick: (isAreaButtonActive: boolean) => void
}

interface DeleteMeasureProps {
  onDelete: () => void
  popupDispatcher: IPopupDispatcher
}

const measuringStyle = new Style({
  fill: new Fill({
    color: 'rgba(255, 255, 255, 0.2)'
  }),
  stroke: new Stroke({
    color: 'rgba(0, 0, 0, 0.5)',
    lineDash: [10, 10],
    width: 2
  }),
  image: new CircleStyle({
    radius: 5,
    stroke: new Stroke({
      color: 'rgba(0, 0, 0, 0.7)'
    }),
    fill: new Fill({
      color: 'rgba(255, 255, 255, 0.2)'
    })
  })
})

const DeleteMeasure: React.FC<DeleteMeasureProps> = ({onDelete, popupDispatcher}) => (
  <div style={{display: 'flex', gap: '8px'}}>
    <button
      type="button"
      className="button width outline"
      onClick={() => popupDispatcher.removePopups(['deleteAreaMeasure'])}
    >
      Отказ
    </button>
    <br />
    <button
      type="button"
      className="button width"
      onClick={() => {
        onDelete()
        popupDispatcher.removePopups(['deleteAreaMeasure'])
      }}
    >
      Изтрий
    </button>
  </div>
)

// Module-level mutable refs for the floating tooltip (driven by OpenLayers, not React state).
let measureTooltipElement: HTMLElement | null = null
let measureTooltipOverlay: Overlay | null = null

export const AreaTool: React.FC<Props> = ({
  left,
  popupDispatcher,
  isAreaButtonActive,
  handleIsAreaButtonStateOnClick
}) => {
  const drawRef = React.useRef<Draw | undefined>(undefined)
  const snapRef = React.useRef<Snap | undefined>(undefined)

  const getMap = () => (getMapService() as MapService)?.map

  const createMeasureTooltip = React.useCallback(
    (feature: Feature) => {
      if (measureTooltipElement) {
        measureTooltipElement.parentNode?.removeChild(measureTooltipElement)
      }

      const overlay = new Overlay({
        offset: [0, -15],
        positioning: 'bottom-center',
        stopEvent: false,
        insertFirst: false
      })
      measureTooltipOverlay = overlay
      measureTooltipElement = document.createElement('div')
      measureTooltipElement.addEventListener('click', e => {
        e.stopPropagation()
        popupDispatcher.addPopups({
          deleteAreaMeasure: {
            content: (
              <DeleteMeasure
                popupDispatcher={popupDispatcher}
                onDelete={() => {
                  const layer = getMapService()
                    .getLayers()
                    .find(l => l.moreInfo.urls[0] == appConfig.reg.measures.url) as VectorLayer
                  layer.getSource().removeFeature(feature as any)
                  getMap()?.removeOverlay(overlay)
                }}
              />
            ),
            header: <div>Изтрий измерването</div>,
            width: '400px'
          }
        })
      })
      measureTooltipElement.className = 'ol-tooltip ol-tooltip-measure'

      overlay.setElement(measureTooltipElement)
      getMap()?.addOverlay(overlay)
    },
    [popupDispatcher]
  )

  const deactivateDraw = React.useCallback(() => {
    if (drawRef.current) {
      getMap()?.removeInteraction(drawRef.current)
    }
    if (snapRef.current) {
      getMap()?.removeInteraction(snapRef.current)
    }
    if (measureTooltipElement) {
      measureTooltipElement.parentNode?.removeChild(measureTooltipElement)
    }
    setTimeout(() => {
      // The effect runs on mount with isAreaButtonActive=false, which calls
      // deactivateDraw() before the map has ever been registered in redux.
      // Guard against a missing mapService so the no-op doesn't throw.
      const ms = getMapService() as MapService | undefined
      if (!ms) return
      ms.startCursorPointer()
      ms.startClickEvent()
    }, 1)
  }, [])

  const activateDraw = React.useCallback(() => {
    ;(getMapService() as MapService).stopCursorPointer()
    ;(getMapService() as MapService).stopClickEvent()

    const source = new VectorSource()
    const draw = new Draw({type: 'Polygon', style: measuringStyle})
    drawRef.current = draw

    const layers = getMapService().getLayers()
    const allActiveFeatures: OlFeature[][] = []
    layers.forEach(l => {
      if (l instanceof VectorLayer) {
        const layerSource = l.getSource()
        if (!layerSource) return
        allActiveFeatures.push((layerSource as VectorSource)?.getFeatures())
      }
    })

    const snap = new Snap({
      source: source as any,
      features: new Collection(allActiveFeatures.flat())
    })
    snapRef.current = snap

    getMap()?.addInteraction(draw)
    getMap()?.addInteraction(snap)

    let drawingTemporaryFeature: OlFeature | null = null
    let listener: EventsKey | undefined
    draw.on('drawstart', evt => {
      drawingTemporaryFeature = evt.feature
      createMeasureTooltip(drawingTemporaryFeature as any)
      let tooltipCoord = (evt as any).coordinate as number[]

      listener = drawingTemporaryFeature?.getGeometry()?.on('change', evt2 => {
        const geom = evt2.target as Polygon
        const output = formatArea(geom)
        tooltipCoord = geom.getInteriorPoint().getCoordinates()
        if (measureTooltipElement) measureTooltipElement.innerHTML = output
        measureTooltipOverlay?.setPosition(tooltipCoord)
      })
    })
    draw.on('drawend', e => {
      const layer = getMapService()
        .getLayers()
        .find(l => l.moreInfo.urls[0] == appConfig.reg.measures.url) as VectorLayer

      layer.getSource().addFeature(e.feature as any)
      if (measureTooltipElement) measureTooltipElement.className = 'ol-tooltip ol-tooltip-static'
      measureTooltipOverlay?.setOffset([0, -7])
      drawingTemporaryFeature = null
      measureTooltipElement = null
      if (listener) {
        unByKey(listener)
      }

      handleIsAreaButtonStateOnClick(false)
    })
  }, [createMeasureTooltip, handleIsAreaButtonStateOnClick])

  React.useEffect(() => {
    if (isAreaButtonActive) {
      activateDraw()
    } else {
      deactivateDraw()
    }
  }, [isAreaButtonActive, activateDraw, deactivateDraw])

  return (
    <div
      className="measure-tool"
      style={{left}}
      title="Измерване"
      onClick={() => handleIsAreaButtonStateOnClick(!isAreaButtonActive)}
    >
      <img
        alt=""
        src="resources/icons/measure-area-white.png"
        style={{width: '28px', height: '28px', backgroundColor: isAreaButtonActive ? '#26751b' : '#5c83b3b5'}}
      />
    </div>
  )
}
